import type { LabelProps } from '@gravity-ui/uikit';
import type { ManagerAuditAction, ManagerAuditEntry, ManagerStatus } from '@odonix/contracts';
import { day, monthTitle, percent, rub } from '../../ui/format.js';

/**
 * Слова журнала и карточек менеджеров. Глагол прошедшего времени без
 * подлежащего: имя менеджера стоит рядом в строке, и «Иванова — позвонила»
 * читается быстрее, чем «менеджер Иванова совершила звонок».
 */
export const MANAGER_ACTION_LABEL: Record<ManagerAuditAction, string> = {
  code_requested: 'запросил код входа',
  login: 'вошёл в кабинет',
  login_failed: 'ввёл неверный код',
  logout: 'вышел из кабинета',
  tenant_viewed: 'открыл карточку',
  tenant_called: 'позвонил',
  tenant_emailed: 'написал письмо',
  manager_created: 'заведён в админке',
  manager_updated: 'изменён в админке',
  manager_disabled: 'отключён',
  manager_enabled: 'включён снова',
  manager_deleted: 'удалён',
  commission_changed: 'процент изменён',
  bonus_added: 'назначена премия',
  bonus_cancelled: 'премия отменена',
  tenant_assigned: 'закреплена клиника',
  tenant_unassigned: 'снята клиника',
  link_created: 'создал ссылку',
  link_updated: 'изменил подпись ссылки',
  link_disabled: 'выключил ссылку',
  link_enabled: 'включил ссылку',
};

/**
 * Звонки и письма — то, ради чего журнал открывают чаще всего («звонили ли
 * этой клинике»), поэтому они выделены; неверный код, отключение и удаление
 * — красные: их ищут взглядом, а не чтением; деньги — зелёные.
 */
export const MANAGER_ACTION_THEME: Record<ManagerAuditAction, NonNullable<LabelProps['theme']>> = {
  code_requested: 'unknown',
  login: 'success',
  login_failed: 'danger',
  logout: 'unknown',
  tenant_viewed: 'unknown',
  tenant_called: 'warning',
  tenant_emailed: 'warning',
  manager_created: 'info',
  manager_updated: 'unknown',
  manager_disabled: 'danger',
  manager_enabled: 'success',
  manager_deleted: 'danger',
  commission_changed: 'success',
  bonus_added: 'success',
  bonus_cancelled: 'danger',
  tenant_assigned: 'info',
  tenant_unassigned: 'unknown',
  link_created: 'info',
  link_updated: 'unknown',
  link_disabled: 'unknown',
  link_enabled: 'info',
};

/** Кто из контактов клиники: в журнале звонка это важнее самого номера. */
export const CONTACT_WHOSE_LABEL: Record<string, string> = {
  clinic: 'клиника',
  owner: 'владелец',
};

export const MANAGER_STATUS_LABEL: Record<ManagerStatus, string> = {
  active: 'Активен',
  disabled: 'Отключён',
  deleted: 'Удалён',
};

export const MANAGER_STATUS_THEME: Record<ManagerStatus, NonNullable<LabelProps['theme']>> = {
  active: 'success',
  disabled: 'unknown',
  deleted: 'danger',
};

const FIELD_LABEL: Record<string, string> = { fullName: 'имя', email: 'почта', phone: 'телефон' };

/** Подробность записи журнала человеческими словами, `null` — подробностей нет. */
export function describeEntry(entry: ManagerAuditEntry): string | null {
  const meta = entry.meta;
  const text = (key: string): string | null => {
    const value = meta[key];
    return typeof value === 'string' && value !== '' ? value : null;
  };
  const num = (key: string): number | null => {
    const value = meta[key];
    return typeof value === 'number' ? value : null;
  };
  const person = (key: string): string | null => {
    const value = meta[key] as { fullName?: unknown } | null | undefined;
    return typeof value?.fullName === 'string' ? value.fullName : null;
  };
  const staff = text('staffEmail');
  const by = (detail: string | null): string | null =>
    [detail, staff].filter((part): part is string => part !== null && part !== '').join(' · ') || null;

  switch (entry.action) {
    case 'tenant_called':
    case 'tenant_emailed': {
      const whose = text('whose');
      return [text('target'), whose === null ? null : (CONTACT_WHOSE_LABEL[whose] ?? null)].filter(Boolean).join(' · ') || null;
    }
    case 'code_requested':
      return meta['delivered'] === false ? 'письмо не ушло' : null;
    case 'manager_updated': {
      const changes = meta['changes'] as Record<string, { from: string | null; to: string | null }> | undefined;
      if (changes !== undefined && Object.keys(changes).length > 0) {
        return by(
          Object.entries(changes)
            .map(([field, change]) => `${FIELD_LABEL[field] ?? field}: ${change.from ?? '—'} → ${change.to ?? '—'}`)
            .join('; '),
        );
      }
      const from = text('from');
      const to = text('to');
      return by(from !== null && to !== null ? `${from} → ${to}` : null);
    }
    case 'commission_changed': {
      const from = num('from');
      const to = num('to');
      const since = text('effectiveFrom');
      return by(
        [from === null || to === null ? null : `${percent(from)} → ${percent(to)}`, since === null ? null : `с ${day(since)}`]
          .filter(Boolean)
          .join(', ') || null,
      );
    }
    case 'bonus_added': {
      const amount = num('amountMinor');
      const month = text('month');
      return by(
        [amount === null ? null : rub(amount), month === null ? null : `за ${monthTitle(month).toLowerCase()}`, text('comment')]
          .filter(Boolean)
          .join(' · ') || null,
      );
    }
    case 'bonus_cancelled': {
      const amount = num('amountMinor');
      return by([amount === null ? null : rub(amount), text('reason')].filter(Boolean).join(' · ') || null);
    }
    case 'tenant_assigned': {
      const source = text('source');
      if (source === 'referral') return `по ссылке ${text('code') ?? ''}`.trim();
      if (source === 'transfer') return by(`передана от ${person('from') ?? 'удалённого менеджера'}`);
      return by(null);
    }
    case 'tenant_unassigned': {
      if (text('reason') === 'manager_deleted') return by('менеджер удалён');
      const to = person('to');
      return by(to === null ? null : `передана ${to}`);
    }
    case 'manager_deleted': {
      const tenants = num('tenants') ?? 0;
      const to = person('transferredTo');
      return by(tenants === 0 ? 'клиник не было' : to === null ? `клиник без менеджера: ${String(tenants)}` : `клиники (${String(tenants)}) переданы ${to}`);
    }
    case 'link_created':
    case 'link_disabled':
    case 'link_enabled':
      return text('code');
    case 'link_updated':
      return [text('code'), `${text('from') ?? '—'} → ${text('to') ?? '—'}`].filter(Boolean).join(' · ');
    default:
      return by(null);
  }
}

/** Адрес кабинета менеджера — ссылка для письма, которое не дошло. */
export function managerAppUrl(): string {
  const configured: unknown = import.meta.env.VITE_MANAGER_APP_URL;
  if (typeof configured === 'string' && configured !== '') return configured;
  // Рядом с админкой на том же домене: admin.odonix.ru → manager.odonix.ru.
  return window.location.origin.replace('//admin.', '//manager.');
}
